import { Injectable } from '@angular/core';

import { ApiService } from './api.service';
import { OrderService } from './order.service';
import { RestaurantService } from './restaurant.service';
import { RestaurantLocalStorageService } from './restaurantLocalStorage.service';

/**
 * Cupom v2 (módulo `new_coupon`): cliente dos endpoints /discount-coupons do server.
 * Com a flag ON o /validate é a fonte de verdade das regras (base elegível, teto,
 * produtos que ativam/bloqueiam, partes de pizza, 1º pedido, limite por cliente,
 * disponibilidade), então o front NÃO recalcula desconto: aplica o valor ABSOLUTO
 * resolvido pelo server. Com a flag OFF nada aqui roda e o fluxo legado
 * (CoupomService, /coupom/check) segue intocado.
 */
@Injectable({ providedIn: 'root' })
export class DiscountCouponService {
    // Código escolhido fora da tela de pagamento (pop-up de destaque no cardápio,
    // bottom-sheet de cupons públicos no carrinho ou link /c/<código>). A aplicação de
    // verdade acontece na tela de pagamento, que valida com o contexto completo do pedido.
    // PERSISTIDO em sessionStorage por loja (QA: recarregar o cardápio não pode evaporar
    // o resgate; o cliente via "resgatado" e chegava ao pagamento sem o cupom). Escopo de
    // ABA de propósito, o mesmo do "já visto" do pop-up: morre ao fechar, não vaza entre
    // lojas nem gruda entre visitas. O campo em memória é fallback para storage indisponível.
    private pendingCodeFallback = '';

    // Chegada pelo link compartilhável (/c/<código>): além do pendente, guarda o anúncio
    // que o cardápio mostra UMA vez ("Cupom X ativado!"). Separado do pendingCode para o
    // toast não disparar de novo em revisitas ao cardápio com o cupom ainda pendente.
    // Em memória de propósito: anúncio é da CHEGADA, recarga não deve re-anunciar.
    private linkAnnounceCode = '';

    // O que o SERVER respondeu sobre o Cupom v2 nesta loja, aprendido das proprias chamadas
    // de cupom em vez de lido do cardapio cacheado (CDN + 24h de localStorage).
    // Persistido por loja: um refresh na tela de pagamento nao pode perder o aprendizado e
    // voltar a rotear pela flag velha.
    private static readonly MODULE_FACT_KEY = 'discountCoupon.v2Live';

    private static readonly MODULE_DISABLED = 'MODULE_DISABLED';

    // Prova de 403 desta ABA. O fato acima continua sendo quem ROTEIA o pagamento; esta
    // marca responde outra pergunta, a de PERGUNTAR de novo. O middleware do server já
    // devolveu MODULE_DISABLED para esta loja, e repetir a consulta a cada entrada de
    // carrinho não muda a resposta: rende 403 no console e, enquanto o TokenInterceptor
    // tratar 403 como sessão morta, um logout por resposta.
    //
    // Escopo de ABA de propósito, não de dispositivo, e é isso que preserva o motivo de o
    // gate ter saído daqui: ligar o módulo para uma loja volta a ser descoberto na visita
    // seguinte, em vez de esperar as 24h do cardápio cacheado. Guardar no localStorage
    // devolveria o problema original em outra forma, com a loja recém-ligada travada num
    // "não" que ninguém revisita.
    private static readonly PROBE_OFF_KEY = 'dc-module-off';

    // Storage indisponível (aba anônima com quota zerada): a marca vive só na instância,
    // que morre com a página. Mesmo racional do pendingCodeFallback.
    private moduleOffFallback = false;

    constructor(
        private apiService: ApiService,
        private orderService: OrderService,
        private restaurantService: RestaurantService,
        private restaurantLocalStorage: RestaurantLocalStorageService,
    ) {}

    /**
     * Telefone mais fresco da sessão: o digitado na etapa de entrega (order.user) vence
     * o salvo em pedidos anteriores (localStorage). É o telefone das regras de CLIENTE
     * do /validate (1º pedido / 1 uso por cliente); vazio = regras puladas pelo server.
     */
    get knownPhone(): string {
        const fromOrder = this.orderService && this.orderService.order && this.orderService.order.user ? this.orderService.order.user.phone : '';

        return fromOrder || this.restaurantLocalStorage.getItem('phone') || '';
    }

    /**
     * Mesmo gate do server: a loja precisa ter cupons habilitados (coupoms_active) e o
     * módulo new_coupon ligado. new_coupon OFF = tela/fluxo antigos, nada de v2.
     *
     * A resposta do SERVER vence o cardápio quando existe. As duas leituras vinham do
     * `cardapio.json`, que é servido por CDN e ainda fica 24h no localStorage: ligar o
     * módulo para uma loja não chegava ao cliente, e como este getter também ROTEIA o
     * pagamento entre v2 e legado (payment.page: `checkCoupon`), a flag velha mandava o
     * cupom v2 ser procurado na tabela antiga. Ver `learnFromServer`.
     */
    get isActive(): boolean {
        const fact = this.readModuleFact();

        if (fact !== null) return fact;

        const info = this.restaurantService.restaurant && this.restaurantService.restaurant.info;

        return !!(info && info.coupoms_active && info.modules && info.modules.new_coupon);
    }

    /**
     * Aprende com a resposta das chamadas de cupom, e só quando ela PROVA alguma coisa:
     *
     * - 403 `MODULE_DISABLED`: o middleware do server barrou. Prova que o módulo está OFF.
     * - lista com pelo menos um cupom: o `availableQuery` do server devolve vazio quando
     *   `coupoms_active` está desligado, então um cupom devolvido prova que os DOIS
     *   interruptores estão ligados.
     * - lista vazia com 200: ambíguo para o `isActive` (loja sem cupom público x cupons
     *   desligados), então não ensina um valor novo. Mas prova uma coisa: o middleware
     *   deixou passar, logo um "OFF" guardado de um 403 anterior está VELHO e sai.
     *
     * Qualquer 200 também levanta a marca de aba que suspende as consultas.
     */
    private learnFromServer(coupons: any[]) {
        // Qualquer 200 prova que o middleware NÃO barrou: a marca desta aba caiu, e voltar
        // a perguntar é de novo o certo.
        this.writeModuleOff(false);

        if (coupons && coupons.length) {
            this.writeModuleFact(true);
            return;
        }

        // 200 com lista vazia não diz se `coupoms_active` está ligado, mas diz que o
        // `new_coupon` está. Se havia um fato NEGATIVO gravado, ele é de um 403 que não
        // vale mais, e mantê-lo rotearia o pagamento para a tabela antiga (payment.page:
        // checkCoupon). Apagar devolve a decisão ao cardápio, que é a resposta honesta
        // para o caso ambíguo. Sem fato gravado, o 200 vazio segue não ensinando nada.
        if (this.readModuleFact() === false) this.clearModuleFact();
    }

    private learnFromRejection(error) {
        if (!error || error.code !== DiscountCouponService.MODULE_DISABLED) return;

        this.learnModuleOff();
    }

    /** O server provou que o módulo está OFF (403 `MODULE_DISABLED` ou `module_enabled: false`). */
    private learnModuleOff() {
        this.writeModuleFact(false);
        this.writeModuleOff(true);
    }

    /** O server já provou, NESTA aba, que o módulo está desligado nesta loja? */
    get moduleProvenOff(): boolean {
        try {
            return sessionStorage.getItem(this.moduleOffStorageKey()) === '1';
        } catch (e) {
            return this.moduleOffFallback;
        }
    }

    private moduleOffStorageKey(): string {
        const info = this.restaurantService.restaurant && this.restaurantService.restaurant.info;

        return `${DiscountCouponService.PROBE_OFF_KEY}-${(info && info.id) || 'boot'}`;
    }

    private writeModuleOff(off: boolean) {
        this.moduleOffFallback = off;
        try {
            if (off) sessionStorage.setItem(this.moduleOffStorageKey(), '1');
            else sessionStorage.removeItem(this.moduleOffStorageKey());
        } catch (e) { /* storage indisponível: fica só o fallback em memória */ }
    }

    /**
     * A MESMA resposta que o middleware daria, servida do que já foi aprendido. Quem chama
     * não distingue "perguntei e levei 403" de "já sei que é 403", então o tratamento de
     * erro do carrinho e do pop-up continua valendo sem uma linha de mudança.
     */
    private static moduleDisabledError() {
        return {
            code: DiscountCouponService.MODULE_DISABLED,
            module: 'new_coupon',
        };
    }

    private readModuleFact(): boolean | null {
        const stored = this.restaurantLocalStorage.getItem(DiscountCouponService.MODULE_FACT_KEY);

        if (stored === '1') return true;
        if (stored === '0') return false;

        return null;
    }

    private writeModuleFact(enabled: boolean) {
        try {
            this.restaurantLocalStorage.setItem(DiscountCouponService.MODULE_FACT_KEY, enabled ? '1' : '0');
        } catch (e) {
            // storage indisponível (aba anônima com quota zerada): segue pelo cardápio
        }
    }

    private clearModuleFact() {
        try {
            this.restaurantLocalStorage.removeItem(DiscountCouponService.MODULE_FACT_KEY);
        } catch (e) {
            // storage indisponível: segue pelo cardápio
        }
    }

    setPendingCode(code: string) {
        this.writePendingCode(code || '');
    }

    get hasPendingCode(): boolean {
        return !!this.readPendingCode();
    }

    peekPendingCode(): string {
        return this.readPendingCode();
    }

    consumePendingCode(): string {
        const code = this.readPendingCode();
        this.writePendingCode('');
        return code;
    }

    private pendingStorageKey(): string {
        const info = this.restaurantService.restaurant && this.restaurantService.restaurant.info;

        return `dc-pending-${(info && info.id) || 'boot'}`;
    }

    private readPendingCode(): string {
        try {
            return sessionStorage.getItem(this.pendingStorageKey()) || '';
        } catch (e) {
            return this.pendingCodeFallback;
        }
    }

    private writePendingCode(code: string) {
        this.pendingCodeFallback = code;
        try {
            if (code) sessionStorage.setItem(this.pendingStorageKey(), code);
            else sessionStorage.removeItem(this.pendingStorageKey());
        } catch (e) { /* storage indisponível: fica só o fallback em memória */ }
    }

    // Entrada do CouponLinkGuard (/c/<código>): deixa pendente E arma o anúncio de chegada.
    setPendingCodeFromLink(code: string) {
        this.setPendingCode(code);
        this.linkAnnounceCode = code || '';
    }

    /** Consome (uma vez) o anúncio de chegada pelo link; vazio se não veio de link. */
    consumeLinkAnnouncement(): string {
        const code = this.linkAnnounceCode;
        this.linkAnnounceCode = '';
        return code;
    }

    // Rejeições que NÃO mudam com o carrinho: manter o pendente só re-mostraria o mesmo
    // erro a cada visita ao pagamento. As demais (MIN_ORDER, PRODUCT_RULE...) são
    // sensíveis ao pedido, e o pendente fica para re-tentar quando o cliente voltar,
    // honrando o "monte seu pedido" prometido na chegada pelo link.
    private static readonly PERMANENT_REJECTIONS = ['NOT_FOUND', 'INACTIVE', 'STORE_DISABLED', 'EXPIRED', 'USAGE_LIMIT', 'PER_CUSTOMER', 'FIRST_ORDER_ONLY'];

    /** Rejeição que NÃO muda com o carrinho (o cupom "caiu"): quem guarda pendente descarta. */
    static isPermanentRejection(errorCode: string): boolean {
        return DiscountCouponService.PERMANENT_REJECTIONS.indexOf(errorCode) !== -1;
    }

    /**
     * Chamado no catch do /validate: decide o destino do pendente após uma rejeição.
     * Só mexe se o código rejeitado É o pendente (comparação no espaço canônico), para a
     * rejeição de um código digitado à mão não matar a escolha do pop-up/link.
     */
    settlePendingAfterRejection(rejectedCode: string, errorCode: string) {
        const pending = this.readPendingCode();
        if (!pending) return;

        const canonical = (value: string) => (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        if (canonical(rejectedCode) !== canonical(pending)) return;

        if (DiscountCouponService.isPermanentRejection(errorCode)) {
            this.writePendingCode('');
        }
    }

    // Cupons marcados como públicos na nova tela (bottom-sheet do carrinho). Erro aqui
    // não pode quebrar o carrinho: resolve com lista vazia.
    /**
     * REJEITA em falha, não resolve com []: lista vazia e lista que não deu para buscar são
     * coisas diferentes para quem chama. Tratadas igual, um soluço de rede fazia o banner de
     * cupons sumir do carrinho como se a loja não tivesse cupom público nenhum.
     */
    listPublic(): Promise<any[]> {
        // Já provado desligado nesta aba: a resposta é conhecida. Este é o ponto que mais
        // repetia, porque o carrinho reconsulta a cada mudança de item.
        if (this.moduleProvenOff) return Promise.reject(DiscountCouponService.moduleDisabledError());

        return new Promise((resolve, reject) => {
            this.apiService.get(`/restaurant/${this.restaurantId()}/discount-coupons/public`).subscribe(
                (response) => {
                    const coupons = (response && response.data) || [];
                    this.learnFromServer(coupons);
                    resolve(coupons);
                },
                (error) => {
                    this.learnFromRejection(error);
                    reject(error);
                },
            );
        });
    }

    // Cupom em destaque (pop-up do cardápio). O server garante no máximo 1 por loja.
    getFeatured(): Promise<any> {
        // Idem: sem pop-up para mostrar, a pergunta só renderia outro 403. A primeira
        // carga do cardápio na aba é que continua perguntando, e é ela que ensina.
        if (this.moduleProvenOff) return Promise.resolve(null);

        return new Promise((resolve) => {
            this.apiService.get(`/restaurant/${this.restaurantId()}/discount-coupons/featured`).subscribe(
                (response) => {
                    // Desde 27/09/2026 o server responde 200 com `module_enabled: false` em loja sem
                    // o módulo, em vez do 403 (que virava erro no console de todo visitante). É o
                    // mesmo fato que o 403 provava; server antigo segue caindo no ramo de erro.
                    if (response && response.module_enabled === false) {
                        this.learnModuleOff();
                        resolve(null);
                        return;
                    }
                    const coupons = (response && response.data) || [];
                    this.learnFromServer(coupons);
                    resolve(coupons[0] || null);
                },
                (error) => {
                    this.learnFromRejection(error);
                    resolve(null); // pop-up não tem estado a perder: degrada em silêncio
                },
            );
        });
    }

    /**
     * Valida o código contra o carrinho. Resolve com o shape do server
     * ({ valid, couponId, code, discountType, discountValue }) — discountValue já é o
     * valor ABSOLUTO (free_shipping devolve a taxa informada em deliveryFee). Rejeita
     * com o body do 422 ({ valid: false, code: ERRO, message }).
     */
    validate(code: string, cart: { subtotal: number; deliveryFee?: number; deliveryType?: string; items?: any[]; paymentMethod?: any }): Promise<any> {
        return new Promise((resolve, reject) => {
            this.apiService
                .post(`/restaurant/${this.restaurantId()}/discount-coupons/validate`, {
                    code: code,
                    phone: this.knownPhone || null,
                    cart: {
                        subtotal: cart.subtotal,
                        deliveryFee: cart.deliveryFee != null ? cart.deliveryFee : null,
                        orderType: this.mapOrderType(cart.deliveryType),
                        items: cart.items || [],
                        // Forma de pagamento quando o cliente JA escolheu (tela de pagamento):
                        // { paymentMethodKey, restaurantCardTypeId?, cardTypeName? }. Sem
                        // escolha, null, e a regra fica para o placement (gate final).
                        paymentMethod: cart.paymentMethod || null,
                    },
                })
                .subscribe(
                    (response) => resolve((response && response.data) || response),
                    (error) => reject(error),
                );
        });
    }

    /**
     * Itens do carrinho (shoppingCartService.products) → contrato de items do /validate:
     * { productId, lineTotal, featured, isPizzaCombo, pizza }. O preço da linha vem de
     * quem conhece o cálculo (shoppingCartService.itemSubtotal), este service não
     * recalcula dinheiro.
     */
    mapItems(products: any[], lineTotalOf: (index: number) => number): any[] {
        return (products || []).map((product, index) => {
            const item: any = {
                productId: product && product.type === 'general' && product.id != null ? Number(product.id) : null,
                lineTotal: Number(lineTotalOf(index)) || 0,
                featured: !!(product && product.featured),
            };

            if (product && product.type === 'pizza') {
                item.pizza = this.pizzaRef(product);
            }

            // Combo de pizza: comboItems são os tamanhos; a regra de bloquear/ativar por
            // parte precisa enxergar dentro (mesmo merge que o server faz no placement).
            if (product && product.type === 'combo' && Array.isArray(product.comboItems) && product.comboItems.length) {
                item.isPizzaCombo = true;
                item.pizza = this.mergePizzaRefs(product.comboItems.map((size) => this.pizzaRef(size)));
            }

            return item;
        });
    }

    // orders.delivery_type do app → ENUM do v2 (delivery/pickup/table). Desconhecido ou
    // ainda não escolhido vira null: regra de tipo não-verificável NÃO rejeita (mesmo
    // comportamento do placement no server).
    mapOrderType(deliveryType?: string): string {
        switch (deliveryType) {
            case 'delivery':
                return 'delivery';
            case 'table':
                return 'table';
            case 'balcony':
            case 'car_takeout':
            case 'pickup':
                return 'pickup';
        }

        return null;
    }

    // Mensagens pt-BR por errorCode do Enforcement (o `message` do server é em inglês).
    static errorMessage(error: any): string {
        const messages = {
            NOT_FOUND: 'Cupom inválido. Verifique se está correto ou ainda disponível.',
            INACTIVE: 'Este cupom não está mais ativo.',
            STORE_DISABLED: 'Os cupons desta loja estão desativados no momento.',
            EXPIRED: 'Este cupom expirou.',
            UNAVAILABLE_TIME: 'Este cupom não está disponível neste dia/horário.',
            USAGE_LIMIT: 'Este cupom atingiu o limite de usos.',
            PER_CUSTOMER: 'Você já usou este cupom.',
            FIRST_ORDER_ONLY: 'Este cupom é válido apenas para o primeiro pedido.',
            MIN_ORDER: 'O pedido não atingiu o valor mínimo deste cupom.',
            ORDER_TYPE: 'Este cupom não vale para este tipo de pedido.',
            FREE_SHIPPING_NO_FEE: 'O frete deste pedido já está grátis, o cupom não é necessário.',
            PAYMENT_METHOD: 'Este cupom não vale para a forma de pagamento escolhida.',
            FEATURED_ITEMS: 'Este cupom não pode ser aplicado em produtos em destaque.',
            PRODUCT_RULE: 'Este cupom não vale para os produtos do seu carrinho.',
            PIZZA_COMBO: 'Este cupom não vale para combos de pizza.',
        };

        return (error && messages[error.code]) || 'Não foi possível aplicar o cupom.';
    }

    private pizzaRef(item: any) {
        // Pós-addProduct o item pizza tem flavors.selected; antes (carrinho recém-montado)
        // ainda é selectedFlavors. Os dois carregam o mesmo shape.
        const flavors = (item && ((item.flavors && item.flavors.selected) || item.selectedFlavors)) || [];
        const extraIds = [];

        flavors.forEach((flavor) => {
            ((flavor && flavor.additionalToppings) || []).forEach((topping) => {
                if (topping && topping.id != null) extraIds.push(Number(topping.id));
            });
        });

        return {
            sizeId: item && item.id != null ? Number(item.id) : null,
            flavorIds: flavors.filter((flavor) => flavor && flavor.id != null).map((flavor) => Number(flavor.id)),
            crustId: item && item.crust && item.crust.id != null ? Number(item.crust.id) : null,
            doughId: item && item.dough && item.dough.id != null ? Number(item.dough.id) : null,
            extraIds: extraIds,
        };
    }

    private mergePizzaRefs(refs: any[]) {
        const merged = { sizeIds: [], flavorIds: [], crustIds: [], doughIds: [], extraIds: [] };

        refs.forEach((ref) => {
            if (ref.sizeId != null) merged.sizeIds.push(ref.sizeId);
            if (ref.crustId != null) merged.crustIds.push(ref.crustId);
            if (ref.doughId != null) merged.doughIds.push(ref.doughId);
            merged.flavorIds = merged.flavorIds.concat(ref.flavorIds);
            merged.extraIds = merged.extraIds.concat(ref.extraIds);
        });

        return merged;
    }

    private restaurantId(): number {
        return this.restaurantService.restaurant.info.id;
    }
}
